// @tier: PREMIUM — Vue admin des incidents chauffeur
import { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import unifiedApi from '../services/unifiedApi';

const DEFAULT_STATS = { total: 0, open: 0, acknowledged: 0, resolved: 0 };
// Référence stable — évite qu'un `?? []` frais à chaque render invalide le
// useMemo de filteredIncidents (dépendance qui semblerait changer sans arrêt).
const EMPTY_INCIDENTS = [];

export const useIncidents = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionStatus, setActionStatus] = useState('acknowledged');
  const [actionNote, setActionNote] = useState('');

  const {
    data: incidents = EMPTY_INCIDENTS,
    isLoading: loading,
    error: queryError,
    refetch: loadData,
  } = useQuery({
    queryKey: ['incidents'],
    queryFn: async () => {
      const r = await unifiedApi.incidents.getAll();
      if (!r.success) throw new Error(r.message || 'Erreur chargement des incidents');
      return r.data || [];
    },
  });

  const error = queryError?.message ?? null;

  const stats = useMemo(
    () =>
      incidents.reduce(
        (acc, incident) => {
          acc.total += 1;
          acc[incident.status] = (acc[incident.status] ?? 0) + 1;
          return acc;
        },
        { ...DEFAULT_STATS },
      ),
    [incidents],
  );

  const filteredIncidents = useMemo(() => {
    let list = incidents;
    if (filterStatus !== 'all') {
      list = list.filter((i) => i.status === filterStatus);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter((i) => {
        const driverName = `${i.drivers?.first_name || ''} ${i.drivers?.last_name || ''}`.toLowerCase();
        return driverName.includes(term) || (i.description || '').toLowerCase().includes(term);
      });
    }
    return list;
  }, [incidents, filterStatus, searchTerm]);

  // Détail chargé à la demande (pas depuis la liste) — l'URL de la photo est
  // signée côté backend à chaque appel, une URL de la liste pourrait être
  // expirée si l'admin la garde ouverte au-delà du TTL.
  const handleViewDetails = async (incident) => {
    setSelectedIncident(incident);
    setShowDetailDialog(true);
    setLoadingDetail(true);
    try {
      const r = await unifiedApi.incidents.getById(incident.id);
      if (r.success) setSelectedIncident(r.data);
    } catch (err) {
      toast.error(err.message || "Erreur lors du chargement de l'incident");
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetailDialog = () => {
    setShowDetailDialog(false);
    setSelectedIncident(null);
  };

  const handleUpdateStatus = (incident) => {
    setSelectedIncident(incident);
    setActionStatus(incident.status === 'open' ? 'acknowledged' : 'resolved');
    setActionNote(incident.resolution_note || '');
    setShowActionDialog(true);
  };

  const closeActionDialog = () => {
    setShowActionDialog(false);
    setSelectedIncident(null);
    setActionNote('');
  };

  const confirmUpdateStatus = async () => {
    if (!selectedIncident) return;

    try {
      const r = await unifiedApi.incidents.update(selectedIncident.id, {
        status: actionStatus,
        resolution_note: actionNote || undefined,
      });
      if (!r.success) {
        toast.error(r.message || 'Erreur lors de la mise à jour');
        return;
      }
      toast.success('Incident mis à jour');
    } catch (err) {
      toast.error(err.message || 'Erreur de connexion — réessayez');
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['incidents'] });
    closeActionDialog();
  };

  return {
    incidents,
    filteredIncidents,
    stats,
    loading,
    error,
    loadData,
    searchTerm,
    setSearchTerm,
    filterStatus,
    setFilterStatus,
    selectedIncident,
    showDetailDialog,
    loadingDetail,
    onDetailDialogOpenChange: (open) => (open ? setShowDetailDialog(true) : closeDetailDialog()),
    showActionDialog,
    onActionDialogOpenChange: (open) => (open ? setShowActionDialog(true) : closeActionDialog()),
    actionStatus,
    setActionStatus,
    actionNote,
    setActionNote,
    handleViewDetails,
    handleUpdateStatus,
    confirmUpdateStatus,
  };
};

export default useIncidents;
